/// <amd-module name="TFS/Views/ProjectOverviewPage/Components/ProjectOverviewPageHeader" />
import * as React from "react";
import { ProjectImage } from "TFS/Views/ProjectOverviewPage/Components/ProjectImage";
import { ProjectVisibilityTag } from "TFS/Views/ProjectOverviewPage/Components/ProjectVisibilityTag";
import * as ProjectOverviewPageResources from "TFS/Views/ProjectOverviewPage/Resources";
import { CurrentUserPermission, ProjectBasicData } from "TFS/Views/ProjectOverviewPage/Shared/Models";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssComponentContext, IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { InviteUsersContext, InviteUsersContextProvider } from "./InviteUsersContext";

export interface AddUserDialogProps {
    showAddUsersDialog: boolean;
    onAddUserDialogDismiss: () => void;
    onAddUsersActionComplete: () => void;
    onAddUserDialogLoadStarted?(): void;
    onAddUserDialogLoadCompleted?(): void;
}

export interface ProjectOverviewPageHeaderProps extends IVssComponentProperties {
    projectBasicData: ProjectBasicData;
    showFavorites: boolean;
    showInviteButton: boolean;
    addUserDialogProps: AddUserDialogProps;
    isHosted: boolean;
    userPermissions: CurrentUserPermission;
    projectImageUrl?: string;
    onInviteButtonClick(): void;
    onEditNameClick(): void;
}

export interface ProjectOverviewPageHeaderState {
    showAddUserDialogContainer: boolean;
    commandBarItems: IReadonlyObservableArray<IHeaderCommandBarItem>;
}

export class ProjectOverviewPageHeader extends VssComponent<ProjectOverviewPageHeaderProps, ProjectOverviewPageHeaderState> {
    constructor(props: ProjectOverviewPageHeaderProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            showAddUserDialogContainer: this.props.showInviteButton && this.props.addUserDialogProps.showAddUsersDialog,
            commandBarItems: this.getCommandBarItems()
        };
    }

    public componentDidUpdate(prevProps: ProjectOverviewPageHeaderProps, prevState: ProjectOverviewPageHeaderState): void {
        super.componentDidUpdate(prevProps, prevState);

        // We need to set showAddUserDialogContainer only when user clicked on AddUserDialog once and the container should be part
        // of the DOM even when add user dialog is dismissed so that we can show notifications/error messages coming from the dialog in the page
        if (this.props.showInviteButton && this.props.addUserDialogProps.showAddUsersDialog && !this.state.showAddUserDialogContainer) {
            this.setState({ showAddUserDialogContainer: true });
        }
    }

    public render(): JSX.Element {
        const {
            className,
            projectBasicData,
            addUserDialogProps,
            showInviteButton,
            showFavorites,
            isHosted,
            onEditNameClick,
            userPermissions,
            projectImageUrl
        } = this.props;
        const { showAddUserDialogContainer } = this.state;
        return (
            <InviteUsersContextProvider
                projectOrTeamDisplayName={projectBasicData.name}
                projectId={projectBasicData.id}
                onUsersAdded={addUserDialogProps.onAddUsersActionComplete}
            >
                <ScreenSizeObserver>
                    {(screenProps: { screenSize: ScreenSize }) => {
                        const smallScreen = screenProps.screenSize === ScreenSize.xsmall;
                        return (
                            <div className={css("po-page-header", className)}>
                                {isHosted && !smallScreen && (
                                    <InviteUsersContext.Consumer>
                                        {contextValue => {
                                            if (!contextValue || !contextValue.useLegacyComponent) {
                                                return null;
                                            }
                                            return (
                                                <div className="add-user-dialog-container" role="presentation">
                                                    <LegacyComponent
                                                        wrappedType="TFS.ProjectOverview.AddUserDialog"
                                                        modules={["ProjectOverview/Scripts/Components/AddUserDialogComponent"]}
                                                        {...addUserDialogProps}
                                                        hidden={!showAddUserDialogContainer}
                                                    />
                                                </div>
                                            );
                                        }}
                                    </InviteUsersContext.Consumer>
                                )}
                                <div
                                    className={css("page-title-container flex-row", smallScreen && "flex-center")}
                                    role="region"
                                    aria-label={ProjectOverviewPageResources.ProjectInformationRegion_Label}
                                >
                                    <div className={css("left-content flex-row flex-center flex-grow scroll-hidden")}>
                                        <div className="project-image-container">
                                            <ProjectImage
                                                projectName={projectBasicData.name}
                                                projectImageUrl={projectImageUrl}
                                            />
                                        </div>
                                        <h1 className={css("project-name-container text-ellipsis", smallScreen ? "title-m" : "title-l")} tabIndex={-1}>
                                            {projectBasicData.name}
                                        </h1>
                                        {userPermissions.isLoggedIn && (
                                            <Button
                                                className={"settings-icon"}
                                                subtle={true}
                                                ariaLabel={ProjectOverviewPageResources.AboutProjectEditButtonAriaLabel}
                                                iconProps={{ iconName: "Settings" }}
                                                onClick={onEditNameClick}
                                            />
                                        )}
                                    </div>
                                    <div className="right-content flex flex-noshrink project-actions-container">
                                        <ProjectVisibilityTag visibility={projectBasicData.visibility} 
                                                              isSmallScreen={smallScreen} 
                                                              className={"proj-visibility-container"} />
                                        {showInviteButton && this.renderInviteUserButton(smallScreen)}
                                        <Observer items={this.state.commandBarItems}>
                                            {(props: { items: IHeaderCommandBarItem[] }) => <HeaderCommandBar items={props.items} />}
                                        </Observer>
                                        {showFavorites && (
                                            <div className={"po-favorite-container"}>
                                                <WrappedComponent
                                                    dependencies={["ms.vss-favorites.favorites-star"]}
                                                    wrappedType={"favoritesStar"}
                                                    wrappedProps={{
                                                        className: "po-favorite font-size-ml",
                                                        favoritedArialabel: ProjectOverviewPageResources.FavoritesButton_Favorited,
                                                        unFavoritedArialabel: ProjectOverviewPageResources.FavoritesButton_UnFavorited,
                                                        artifact: {
                                                            artifactId: projectBasicData.id,
                                                            artifactType: "Microsoft.TeamFoundation.Classification.TeamProject",
                                                            artifactName: projectBasicData.name,
                                                            artifactScope: { id: projectBasicData.id, type: "Project", name: projectBasicData.name }
                                                        }
                                                    }}
                                                />
                                        </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                </ScreenSizeObserver>
            </InviteUsersContextProvider>
        );
    }

    private getCommandBarItems() {
        const contributionsItems = getMenuItems(this.context.pageContext, "ms.vss-tfs-web.project-overview-contributed-header-buttons");
        return contributionsItems;
    }

    private renderInviteUserButton(isSmallScreen:boolean) {
        const { onInviteButtonClick } = this.props;
        return (
            <InviteUsersContext.Consumer>
                {contextValue => {
                    if (!contextValue) {
                        return null;
                    }
                    return (
                        <Button
                            primary={true}
                            className={"invite-button"}
                            ariaLabel={ProjectOverviewPageResources.InviteButtonText}
                            text={isSmallScreen ? "" : ProjectOverviewPageResources.InviteButtonText}
                            tooltipProps={{ text : isSmallScreen ? ProjectOverviewPageResources.InviteButtonText : "" }}
                            iconProps={{ iconName: "AddGroup" }}
                            onClick={() => {
                                onInviteButtonClick();
                                contextValue.onFlowStart();
                            }}
                        />
                    );
                }}
            </InviteUsersContext.Consumer>
        );
    }
}
